import { canManagePortalUsers } from "@/services/jambushrustiAuth";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@jambushrushti/lib/utils";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import { getAdminSession } from "@jambushrushti/services/adminAuth";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Globe,
  Image,
  FileText,
  PenTool,
  Calendar,
  Search,
  Building,
  Brain,
  Key,
  ClipboardList,
  Settings,
  LogOut,
  X,
  Newspaper,
  Share2,
  MessageSquare,
  Video,
  Ticket,
  Mail,
} from "lucide-react";
import { Button } from "@jambushrushti/components/ui/button";
import {
  LotusRule,
  TempleGateLogo,
} from "@jambushrushti/site/components/Ornaments";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@jambushrushti/components/ui/avatar";

const navGroups = [
  {
    groupLabel: "MAIN OVERVIEW",
    items: [
      {
        icon: LayoutDashboard,
        label: "Dashboard",
        href: "/vault-cms-admin/dashboard",
      },
      { icon: Users, label: "Users & Roles", href: "/vault-cms-admin/users" },
    ],
  },
  {
    groupLabel: "KNOWLEDGE & CONTENT",
    items: [
      { icon: BookOpen, label: "Guruvani", href: "/vault-cms-admin/guruvani" },
      { icon: Globe, label: "Website CMS", href: "/vault-cms-admin/cms" },
      { icon: FileText, label: "Books (PDFs)", href: "/vault-cms-admin/books" },
      // {
      //   icon: Newspaper,
      //   label: "Articles & Tributes",
      //   href: "/admin/articles",
      // },
      { icon: PenTool, label: "Blog Posts", href: "/vault-cms-admin/blog" },
      {
        icon: FileText,
        label: "eMagazine",
        href: "/vault-cms-admin/emagazine",
      },
      { icon: Image, label: "Media Gallery", href: "/vault-cms-admin/gallery" },
    ],
  },
  {
    groupLabel: "COMMUNITY & ENGAGEMENT",
    items: [
      { icon: Calendar, label: "Events", href: "/vault-cms-admin/events" },
      {
        icon: Video,
        label: "Live Telecast",
        href: "/vault-cms-admin/live-telecast",
      },
      // { icon: Share2, label: "Social Media", href: "/admin/social-media" },
      {
        icon: MessageSquare,
        label: "Visitor Feedback",
        href: "/vault-cms-admin/feedback",
      },
      {
        icon: Mail,
        label: "Contact Us",
        href: "/vault-cms-admin/contact-us",
      },
      // { icon: Ticket, label: "Ticket Management", href: "/tickets/dashboard" },
    ],
  },
  // {
  //   groupLabel: "ECOSYSTEM & SYSTEM",
  //   items: [
  //     { icon: Search, label: "Research Tools", href: "/admin/research" },
  //     { icon: Building, label: "Digital Museum", href: "/admin/museum" },
  //     { icon: Brain, label: "AI & Indexing", href: "/admin/ai" },
  //     { icon: Key, label: "Access Requests", href: "/admin/access-requests" },
  //     { icon: ClipboardList, label: "Audit Logs", href: "/admin/logs" },
  //     { icon: Settings, label: "System Settings", href: "/admin/settings" },
  //   ],
  // },
];

interface AdminSidebarProps {
  onClose?: () => void;
}

export function AdminSidebar({ onClose }: AdminSidebarProps) {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const { user, logout } = useAdminAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/vault-cms-admin";
  };

  return (
    <aside
      className="sticky top-0 z-30 flex h-screen w-72 flex-col border-r border-[#3e1a06] text-[#f8ead0] shadow-xl lg:w-64"
      style={{ background: "var(--gradient-sidebar)" }}
    >
      {/* gold hairline along the inner edge, like a book's spine (as on the sign-in panel) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-[3px] opacity-60"
        style={{
          background:
            "linear-gradient(180deg, transparent, #c8964a 15%, #c8964a 85%, transparent)",
        }}
      />

      {/* Logo/Brand */}
      <div className="relative flex flex-shrink-0 items-center justify-between border-b border-white/10 px-4 py-5">
        <Link
          to="/vault-cms-admin/dashboard"
          className="flex items-center gap-3"
          onClick={onClose}
        >
          <TempleGateLogo className="h-11 w-10 flex-shrink-0 text-[#d9a95f]" />
          <div className="min-w-0">
            <h1 className="font-display text-[19px] font-semibold leading-tight tracking-wide text-[#fbecd0]">
              Jambushrushti
            </h1>
            <LotusRule className="my-1 block h-2.5 w-28" />
            <p className="whitespace-nowrap text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[#e2b86a]">
              Content Administration
            </p>
          </div>
        </Link>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="text-[#f8ead0] hover:bg-white/10 hover:text-white lg:hidden"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* User Info */}
      <div className="relative flex-shrink-0 border-b border-white/10 px-4 py-3.5">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 border border-[#c8964a]/70">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-gradient-to-b from-[#8c3c15] to-[#6a280b] text-xs font-bold text-[#fdf1e2]">
              {user?.name?.charAt(0).toUpperCase() || "?"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-[#fbecd0]">
              {user?.name || "Administrator"}
            </p>
            <p className="truncate text-[11px] text-[#f8ead0]/60">
              {user?.email || "admin@jambushrushti.org"}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative flex-shrink-0 px-3 pt-3 lg:hidden">
        <div className="group relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#f8ead0]/50 group-focus-within:text-[#e2b86a]" />
          <input
            type="text"
            placeholder="Search console..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-black/15 py-2 pl-8 pr-3 text-xs text-[#f8ead0] placeholder:text-[#f8ead0]/45 focus:border-[#c8964a]/60 focus:outline-none"
          />
        </div>
      </div>

      {/* Categorized Navigation - Independent Smooth Scroll */}
      <nav className="custom-scrollbar relative min-h-0 flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {(getAdminSession()?.can_access_cms ? navGroups : []).map(group => (
          <div key={group.groupLabel} className="space-y-1">
            <h3 className="mb-1.5 px-3 font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-[#e2b86a]/80">
              {group.groupLabel}
            </h3>
            {group.items
              .filter(
                item =>
                  (item.href !== "/vault-cms-admin/users" ||
                    canManagePortalUsers(getAdminSession())) &&
                  item.label
                    .toLowerCase()
                    .includes(searchQuery.trim().toLowerCase())
              )
              .map(item => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href !== "/vault-cms-admin/dashboard" &&
                    location.pathname.startsWith(item.href));

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13px] font-medium transition-all duration-200",
                      isActive
                        ? "bg-gradient-to-b from-[#fbf0dc] to-[#f1dfbd] font-semibold text-[#5e2208] shadow-[inset_3px_0_0_#ca9f36,0_4px_12px_-4px_rgba(0,0,0,0.35)]"
                        : "text-[#f8ead0]/80 hover:bg-white/10 hover:text-[#fbecd0]"
                    )}
                  >
                    <item.icon
                      className={cn(
                        "h-[18px] w-[18px] flex-shrink-0",
                        isActive ? "text-[#8c3c15]" : "text-[#f8ead0]/70"
                      )}
                    />
                    <span className="truncate text-[13.5px]">{item.label}</span>
                  </Link>
                );
              })}
          </div>
        ))}
      </nav>

      {/* Logout */}
      <div className="relative flex-shrink-0 border-t border-white/10 p-3">
        <Button
          variant="ghost"
          className="h-10 w-full justify-start rounded-lg text-[13px] font-medium text-[#f8ead0]/80 hover:bg-white/10 hover:text-[#fbecd0]"
          onClick={handleLogout}
        >
          <LogOut className="mr-2.5 h-[18px] w-[18px] text-[#f8ead0]/70" />
          Sign Out Console
        </Button>
      </div>
    </aside>
  );
}
